import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getToken } from '../../../features/token/tokenSaver';
import { getAdminAdDisplayLogs } from '../../../services/getAdminAdDisplayLogs';
import './AdminAdDisplayLogs.css';

const PAGE_SIZE = 50;

const emptyFilters = () => ({
    from: '',
    to: '',
    screenId: '',
    advertiserId: '',
    adId: ''
});

export default function AdminAdDisplayLogs() {
    const token = useSelector(getToken);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [draft, setDraft] = useState(emptyFilters);
    const [applied, setApplied] = useState(emptyFilters);

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        const params = {
            limit: PAGE_SIZE,
            offset,
            from: applied.from || undefined,
            to: applied.to || undefined,
            screenId: applied.screenId || undefined,
            advertiserId: applied.advertiserId || undefined,
            adId: applied.adId || undefined
        };
        getAdminAdDisplayLogs(token, params)
            .then((res) => {
                setRows(res.data.rows || []);
                setTotal(typeof res.data.total === 'number' ? res.data.total : 0);
            })
            .catch(() => {
                setError('Could not load display logs. Try again or re-authenticate.');
                setRows([]);
                setTotal(0);
            })
            .finally(() => setLoading(false));
    }, [token, offset, applied]);

    useEffect(() => {
        load();
    }, [load]);

    function applyFilters() {
        setApplied({ ...draft });
        setOffset(0);
    }

    function clearFilters() {
        const cleared = emptyFilters();
        setDraft(cleared);
        setApplied(cleared);
        setOffset(0);
    }

    const formatTime = (t) => {
        if (!t) return '—';
        try {
            return new Date(t).toLocaleString();
        } catch (e) {
            return String(t);
        }
    };

    const end = rows.length ? offset + rows.length : offset;
    const hasPrev = offset > 0;
    const hasNext = offset + PAGE_SIZE < total;

    return (
        <div className="adminDashboardContainer">
            <div className="adDisplayLogsPage">
                <div className="adDisplayLogsHeader">
                    <h1 className="adDisplayLogsTitle">Ad display log</h1>
                    <span className="adDisplayLogsSubtitle">
                        Every time a screen shows a campaign slide, a row is recorded here.
                    </span>
                </div>

                {error && <div className="adDisplayLogsError">{error}</div>}

                <div className="adDisplayLogsFilters">
                    <div className="adDisplayLogsField">
                        <label htmlFor="adlog-from">From</label>
                        <input
                            id="adlog-from"
                            type="datetime-local"
                            value={draft.from}
                            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                        />
                    </div>
                    <div className="adDisplayLogsField">
                        <label htmlFor="adlog-to">To</label>
                        <input
                            id="adlog-to"
                            type="datetime-local"
                            value={draft.to}
                            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                        />
                    </div>
                    <div className="adDisplayLogsField">
                        <label htmlFor="adlog-screen">Screen ID</label>
                        <input
                            id="adlog-screen"
                            type="number"
                            placeholder="Any"
                            value={draft.screenId}
                            onChange={(e) => setDraft((d) => ({ ...d, screenId: e.target.value }))}
                        />
                    </div>
                    <div className="adDisplayLogsField">
                        <label htmlFor="adlog-advertiser">Advertiser ID</label>
                        <input
                            id="adlog-advertiser"
                            type="number"
                            placeholder="Any"
                            value={draft.advertiserId}
                            onChange={(e) => setDraft((d) => ({ ...d, advertiserId: e.target.value }))}
                        />
                    </div>
                    <div className="adDisplayLogsField">
                        <label htmlFor="adlog-ad">Campaign ID</label>
                        <input
                            id="adlog-ad"
                            type="number"
                            placeholder="Any"
                            value={draft.adId}
                            onChange={(e) => setDraft((d) => ({ ...d, adId: e.target.value }))}
                        />
                    </div>
                    <div className="adDisplayLogsActions">
                        <button type="button" className="adDisplayLogsBtn adDisplayLogsBtnGhost" onClick={clearFilters}>
                            Reset
                        </button>
                        <button type="button" className="adDisplayLogsBtn adDisplayLogsBtnPrimary" onClick={applyFilters}>
                            Apply
                        </button>
                    </div>
                </div>

                <div className="adDisplayLogsSummary">
                    {loading ? 'Loading…' : `Showing ${rows.length ? offset + 1 : 0}–${end} of ${total} events`}
                </div>

                <div className="adDisplayLogsTableWrap">
                    {!loading && rows.length === 0 && (
                        <div className="adDisplayLogsEmpty">No display events match these filters.</div>
                    )}
                    {(loading || rows.length > 0) && (
                        <table className="adDisplayLogsTable">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Campaign</th>
                                    <th>Advertiser</th>
                                    <th>Screen</th>
                                    <th>Paid</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={5}>Loading…</td>
                                    </tr>
                                )}
                                {!loading &&
                                    rows.map((r) => (
                                        <tr key={r.adLogId}>
                                            <td>{formatTime(r.timeShown)}</td>
                                            <td>
                                                {r.campaignName || '—'}
                                                <div className="adDisplayLogsMuted">Ad #{r.adId}</div>
                                            </td>
                                            <td>
                                                {r.advertiserName || '—'}
                                                <div className="adDisplayLogsMuted">ID {r.advertiserId}</div>
                                            </td>
                                            <td>
                                                Screen #{r.screenId}
                                                {r.screenAddress ? (
                                                    <div className="adDisplayLogsMuted">{r.screenAddress}</div>
                                                ) : null}
                                                {r.ownerCity ? (
                                                    <div className="adDisplayLogsMuted">{r.ownerCity}</div>
                                                ) : null}
                                            </td>
                                            <td>{r.payed ? 'Yes' : 'No'}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="adDisplayLogsPagination">
                    <button
                        type="button"
                        className="adDisplayLogsBtn adDisplayLogsBtnGhost"
                        disabled={!hasPrev || loading}
                        onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        className="adDisplayLogsBtn adDisplayLogsBtnGhost"
                        disabled={!hasNext || loading}
                        onClick={() => setOffset(offset + PAGE_SIZE)}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
}
